import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toCanonicalWorkspacePath } from './siteHosts';

export const ALL_DEPARTMENTS_SCOPE = '__all__';

/** Workspace routes where department scope does not apply (global / settings pages). */
export const NON_DEPARTMENT_WORKSPACE_PREFIXES = [
  '/workspace/docs',
  '/workspace/display',
  '/workspace/employees',
  '/workspace/roles',
  '/workspace/customers',
  '/workspace/suppliers',
  '/workspace/site-content',
  '/workspace/hero',
  '/workspace/feedback-inbox',
  '/workspace/testimonials',
  '/workspace/account-deletion-retention',
  '/workspace/payments',
  '/workspace/finance/delivery-costs',
  '/workspace/finance/refunds',
  '/workspace/finance/reconciliation',
  '/workspace/activities',
  '/workspace/security/action-audit',
  '/workspace/reports/analytics',
  '/workspace/order-statistics',
  '/workspace/promotions',
  '/workspace/orders',
  '/workspace/delivery/scan',
  '/workspace/delivery-batches',
  '/workspace/landing-promotion',
  '/workspace/recommendations',
  '/workspace/checkout-marketing',
  '/workspace/department-management',
  '/workspace/currencies',
  '/workspace/delivery-pricing',
  '/workspace/geo-access',
  '/workspace/mail-routing',
  '/workspace/cron-jobs',
  '/workspace/app-parameters',
  '/workspace/carrier-accounts',
  '/workspace/ship-from',
  '/workspace/referrals',
  '/workspace/profile',
];

export function isDepartmentScopedWorkspacePath(pathname: string): boolean {
  const canonical = toCanonicalWorkspacePath(pathname);
  if (!/^\/workspace(?:\/|$)/i.test(canonical)) return false;
  return !NON_DEPARTMENT_WORKSPACE_PREFIXES.some(
    (prefix) => canonical === prefix || canonical.startsWith(`${prefix}/`),
  );
}

export type WorkspaceDepartment = {
  id: string;
  slug: string;
  name: string;
};

let cachedWorkspaceDepartments: WorkspaceDepartment[] | null = null;

export function setWorkspaceDepartmentScopeValue(nextScope: string): void {
  if (typeof window === 'undefined') return;
  const normalized = nextScope.trim() || ALL_DEPARTMENTS_SCOPE;
  window.localStorage.setItem('workspace_department_scope', normalized);
  window.dispatchEvent(
    new CustomEvent('workspace-department-scope:change', {
      detail: { scope: normalized },
    }),
  );
}

type UseWorkspaceDepartmentScopeOptions = {
  accessToken?: string | null;
  enabled?: boolean;
  isAdmin?: boolean;
};

export function useWorkspaceDepartmentScope({
  accessToken,
  enabled = true,
  isAdmin = false,
}: UseWorkspaceDepartmentScopeOptions) {
  const { t } = useTranslation();
  const [departmentScope, setDepartmentScopeState] = useState<string>(() => {
    if (typeof window === 'undefined') return ALL_DEPARTMENTS_SCOPE;
    return window.localStorage.getItem('workspace_department_scope') || ALL_DEPARTMENTS_SCOPE;
  });
  const [workspaceDepartments, setWorkspaceDepartments] = useState<WorkspaceDepartment[]>(
    () => cachedWorkspaceDepartments || [],
  );

  useEffect(() => {
    const readScope = () =>
      typeof window !== 'undefined'
        ? window.localStorage.getItem('workspace_department_scope') || ALL_DEPARTMENTS_SCOPE
        : ALL_DEPARTMENTS_SCOPE;
    const handleScopeChange = () => {
      setDepartmentScopeState(readScope());
    };
    window.addEventListener('workspace-department-scope:change', handleScopeChange as EventListener);
    window.addEventListener('storage', handleScopeChange);
    return () => {
      window.removeEventListener(
        'workspace-department-scope:change',
        handleScopeChange as EventListener,
      );
      window.removeEventListener('storage', handleScopeChange);
    };
  }, []);

  useEffect(() => {
    if (!enabled || !accessToken) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE || 'http://localhost:3001'}/departments/workspace`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as WorkspaceDepartment[];
        if (!cancelled && Array.isArray(data)) {
          cachedWorkspaceDepartments = data;
          setWorkspaceDepartments(data);
        }
      } catch {
        if (!cancelled) setWorkspaceDepartments([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, enabled]);

  const setDepartmentScope = useCallback((nextScope: string) => {
    const normalized = nextScope.trim() || ALL_DEPARTMENTS_SCOPE;
    setDepartmentScopeState(normalized);
    setWorkspaceDepartmentScopeValue(normalized);
  }, []);

  useEffect(() => {
    if (!enabled || isAdmin) return;
    if (workspaceDepartments.length === 0) return;
    if (departmentScope === ALL_DEPARTMENTS_SCOPE) {
      setDepartmentScope(workspaceDepartments[0].slug);
      return;
    }
    const stillAllowed = workspaceDepartments.some((dep) => dep.slug === departmentScope);
    if (!stillAllowed) {
      setDepartmentScope(workspaceDepartments[0].slug);
    }
  }, [
    departmentScope,
    enabled,
    isAdmin,
    setDepartmentScope,
    workspaceDepartments,
  ]);

  const scopeLabel = useMemo(() => {
    const scope = String(departmentScope || '').trim().toLowerCase();
    if (!scope || scope === ALL_DEPARTMENTS_SCOPE) {
      return t('header.department_scope_all', { defaultValue: 'All departments' });
    }
    const match = workspaceDepartments.find((dep) => dep.slug === scope);
    return match?.name || scope;
  }, [departmentScope, t, workspaceDepartments]);

  return {
    departmentScope,
    workspaceDepartments,
    setDepartmentScope,
    scopeLabel,
  };
}

/** @deprecated Use `useWorkspaceDepartmentScope` */
export function useWorkspaceScopeBadgeLabel(
  accessToken?: string | null,
  enabled = true,
): string {
  const { scopeLabel } = useWorkspaceDepartmentScope({ accessToken, enabled });
  return scopeLabel;
}
